// File: src/pages/PaymentSuccessPage.tsx
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useActions } from '@/contexts/ActionContext';
import { Confetti, ConfettiRef } from '@/components/ui/confetti';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { verifyStripePayment } from '@/lib/stripe';

// Persist a simple lock so we don’t re-run a session twice
const getVerificationLock = (sessionId: string): boolean => {
  return localStorage.getItem(`verification_lock_${sessionId}`) === 'true';
};
const setVerificationLock = (sessionId: string): void => {
  localStorage.setItem(`verification_lock_${sessionId}`, 'true');
};

const PaymentSuccessPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { currentUser, loading: authLoading } = useAuth();
  const { addUserActions } = useActions();
  const confettiRef = useRef<ConfettiRef>(null);

  const [isVerifying, setIsVerifying] = useState(true);
  const [verificationLockChecked, setVerificationLockChecked] = useState(false);
  const [verificationResult, setVerificationResult] = useState<{
    success: boolean;
    actionsAdded?: number;
    amount?: number;
    message?: string;
  } | null>(null);

  const sessionId = searchParams.get('session_id');

  // Launch confetti effect
  const triggerConfetti = () => {
    if (!confettiRef.current) return;
    const duration = 3000;
    const end = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };
    const randomInRange = (min: number, max: number) => Math.random() * (max - min) + min;

    const interval = setInterval(() => {
      const timeLeft = end - Date.now();
      if (timeLeft <= 0) return clearInterval(interval);
      const particleCount = 50 * (timeLeft / duration);
      confettiRef.current!.fire({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
      });
      confettiRef.current!.fire({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
      });
    }, 250);
  };

  // First pass: check if we've already verified this session
  useEffect(() => {
    if (!sessionId) {
      setVerificationLockChecked(true);
      setIsVerifying(false);
      toast({
        title: 'Error',
        description: 'No session ID found. Please try again.',
        variant: 'destructive',
      });
      navigate('/forge');
      return;
    }

    const locked = getVerificationLock(sessionId);
    if (locked) {
      setVerificationResult({
        success: true,
        actionsAdded: 100,
        amount: 1,
        message: 'Payment was already verified',
      });
      setIsVerifying(false);
    }

    setVerificationLockChecked(true);
  }, [sessionId, navigate, toast]);

  // Actual verification against Stripe
  useEffect(() => {
    if (!verificationLockChecked || !sessionId || verificationResult) return;
    if (!isVerifying) return;

    const verifySession = async () => {
      try {
        const userId = currentUser?.uid || localStorage.getItem('userId');
        if (!userId) {
          setVerificationResult({ success: false, message: 'User ID missing; please sign in.' });
          setIsVerifying(false);
          return;
        }

        const result = await verifyStripePayment(sessionId, userId);
        if (result.success && result.actionsAdded) {
          setVerificationLock(sessionId);

          // Update local context state (will skip DB re-write if already processed)
          if (currentUser) {
            try {
              await addUserActions(result.actionsAdded, sessionId!, result.amount);
            } catch (err) {
              console.error('Local state update failed:', err);
            }
          }

          toast({
            title: 'Payment Successful',
            description: `${result.actionsAdded} actions have been added to your account!`,
            variant: 'default',
          });

          triggerConfetti();
        }

        setVerificationResult(result);
      } catch (err: any) {
        toast({
          title: 'Verification Error',
          description: err.message || 'Failed to verify payment.',
          variant: 'destructive',
        });
        setVerificationResult({ success: false, message: err.message });
      } finally {
        setIsVerifying(false);
      }
    };

    if (currentUser) localStorage.setItem('userId', currentUser.uid);
    verifySession();
  }, [currentUser, sessionId, toast, verificationLockChecked, verificationResult, isVerifying]);

  const handleRetry = () => {
    setIsVerifying(true);
    setVerificationResult(null);
  };

  const handleSignIn = () => {
    const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
    navigate(`/?returnTo=${returnUrl}`);
  };

  return (
    <div className="container mx-auto px-4 py-16 flex flex-col items-center justify-center min-h-[80vh]">
      <Confetti
        ref={confettiRef}
        className="fixed inset-0 w-full h-full z-50 pointer-events-none"
      />

      <div className="bg-card border border-border rounded-xl shadow-lg p-8 max-w-md w-full text-center">
        <h1 className="text-3xl font-bold mb-6">Payment Status</h1>

        {!verificationLockChecked || isVerifying ? (
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-16 w-16 animate-spin text-primary" />
            <p className="text-lg">Verifying your payment...</p>
          </div>
        ) : verificationResult?.success ? (
          <div className="flex flex-col items-center gap-4">
            <CheckCircle className="h-16 w-16 text-green-500" />
            <h2 className="text-2xl font-bold text-green-500">Payment Successful!</h2>
            <p className="text-lg mb-2">
              Thank you for your purchase. {verificationResult.actionsAdded} actions have been added
              to your account.
            </p>
            <p className="text-sm text-muted-foreground mb-6">
              Transaction ID: {sessionId?.substring(0, 8)}...
            </p>
            <Button size="lg" className="w-full" onClick={() => navigate('/forge')}>
              Return to Forge
            </Button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-4">
            <AlertCircle className="h-16 w-16 text-red-500" />
            <h2 className="text-2xl font-bold text-red-500">Payment Verification Failed</h2>
            <p className="text-lg mb-6">
              {verificationResult?.message || 'There was an issue verifying your payment.'}
            </p>
            <div className="flex flex-col gap-3 w-full">
              {!currentUser && (
                <Button size="lg" className="w-full" onClick={handleSignIn}>
                  Sign In
                </Button>
              )}
              <Button size="lg" className="w-full" onClick={handleRetry}>
                Try Again
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="w-full"
                onClick={() => navigate('/forge')}
              >
                Return to Forge
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
